// Self — Oreia's memory of you, written as a portrait you can read, affirm, correct or erase.
//
// Four promises hold this screen up, and they are product requirements rather than copy:
//   TRACEABLE   every observation names the entries it came from; one that can't never renders.
//   CORRECTABLE every memory can be affirmed, rewritten in your words, or forgotten for good.
//   PRIVATE     encrypted at rest, never shared — the note at the bottom ships as designed.
//   GENTLE      describe, never diagnose. Threads are tending, softening, emerging. Never problems.
//
// Layout follows the handoff: one column on a phone, and from 1080px a main column of memories and
// journey beside a sticky rail holding the blend, milestones, threads and the privacy note. The
// mood instruments, check-ins and symbol vault keep their place in the main column — they are the
// evidence the portrait is built from, and this screen would be a claim without them.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEntries } from '../entries/EntriesProvider';
import { useSession } from '../auth/SessionProvider';
import { usePortrait } from '../portrait/usePortrait';
import {
  adjustObservation,
  byProminence,
  fetchAnalysisRun,
  isSounding,
  forgetObservation,
  refreshPortrait,
  respondToArchetype,
  respondToObservation,
  type ActivationLevel,
  type AnalysisRun,
  type Observation,
} from '../api/portrait';
import {
  fetchCheckIns,
  patternLabel,
  respondToCheckIn,
  type PatternCheckIn,
} from '../api/check-ins';
import { CheckInCard } from './CheckInCard';
import {
  fetchMoodEvidence,
  fetchMoodWeather,
  type MoodEvidence,
  type MoodEvidenceClaim,
  type MoodEvidenceSource,
  type MoodWeather,
} from '../api/mood';
import { fetchMilestones, type MilestonesView } from '../api/rewards';
import { EmptyState, Spinner } from '../components/ui';
import { CardArt } from '../components/CardArt';
import {
  EmotionalShiftChart,
  MoodPresenceMandala,
  MoodSpectrumPath,
  presenceFromMood,
} from '../components/SelfStats';
import {
  IconChevronRight,
  IconCheck,
  IconClose,
  IconArrow,
  IconLock,
  IconOreia,
  IconSliders,
} from '../components/Icons';
import { countedSymbols } from './home-support';
import { SymbolOrb } from '../symbols/SymbolOrb';
import { MemoryCard } from '../self/MemoryCard';
import { AdjustMemorySheet } from '../self/AdjustMemorySheet';
import { UpdatePanel, type UpdateState } from '../self/UpdatePanel';
import { journeyFromArchetypes } from '../self/journey';
import { pendingCards } from '../self/review-deck';
import { useToast, JournalToast } from '../journals/toast';



/** The blend's vocabulary, in place of a percentage the underlying score can't honestly support. */
const ACTIVATION_WORD: Record<ActivationLevel, string> = {
  dominant: 'strongest',
  active: 'active',
  signal: 'a signal',
  dormant: 'quiet',
};

/** How a pattern's state is described. Tending, never failing — see the gentleness promise. */
function threadState(trajectory: string | null, occurrences: number): string {
  if (trajectory === 'rising') return 'emerging';
  if (trajectory === 'fading') return 'softening';
  return occurrences >= 6 ? 'steady' : 'tending';
}

export function SelfScreen() {
  const { entries } = useEntries();
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;
  const portrait = usePortrait();
  const { toast, ping } = useToast();

  const [updateState, setUpdateState] = useState<UpdateState>('idle');
  const [run, setRun] = useState<AnalysisRun | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, Partial<Observation>>>({});
  const [adjusting, setAdjusting] = useState<Observation | null>(null);
  const [correctingPortrait, setCorrectingPortrait] = useState(false);
  const [mood, setMood] = useState<MoodWeather | null>(null);
  const [evidence, setEvidence] = useState<MoodEvidence | null>(null);
  const [moodLoading, setMoodLoading] = useState(true);
  const [milestones, setMilestones] = useState<MilestonesView | null>(null);
  const [checkIns, setCheckIns] = useState<PatternCheckIn[]>([]);
  const [totalOpenCheckIns, setTotalOpenCheckIns] = useState(0);
  const [answeredCheckIns, setAnsweredCheckIns] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState<{
    title: string;
    why: string;
    sources: MoodEvidenceSource[];
  } | null>(null);

  const symbols = useMemo(() => countedSymbols(entries), [entries]);
  const entryTitle = useCallback(
    (id: string) => entries.find((e) => e.id === id)?.title?.trim() || null,
    [entries],
  );

  // Local corrections win over the server list until the next reload, so affirming or rewriting a
  // memory settles instantly instead of waiting on a round trip that might not change anything.
  const observations = portrait.observations
    .filter((o) => !dismissed.has(o.id))
    .map((o) => (edits[o.id] ? { ...o, ...edits[o.id] } : o));

  // What the deck would ask about. Only the count is needed here — the deck loads its own queue, so
  // that a card answered there doesn't depend on this screen having been mounted.
  const pending = useMemo(() => pendingCards(observations), [observations]);

  const openCheckIns = checkIns.filter((c) => !answeredCheckIns.has(c.id));

  const todayMood = useMemo(() => {
    if (!mood) return null;
    const today = mood.weather.find((d) => d.day === mood.today);
    return today?.selfMood ?? null;
  }, [mood]);

  const claimByLabel = useMemo(() => {
    const map = new Map<string, MoodEvidenceClaim>();
    for (const c of evidence?.claims ?? []) {
      map.set(c.label.toLowerCase(), c);
      map.set(c.canonicalId.toLowerCase(), c);
    }
    return map;
  }, [evidence]);

  const reloadMood = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await fetchMoodWeather(14, signal);
      if (!signal?.aborted) setMood(next);
    } catch {
      if (!signal?.aborted) setMood(null);
    } finally {
      if (!signal?.aborted) setMoodLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setMoodLoading(true);
    void reloadMood(controller.signal);
    return () => controller.abort();
  }, [reloadMood]);

  // The last run's receipt, so someone arriving on the screen can still see what the last update
  // did rather than only the person who happened to be watching when it finished.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const [last, badges] = await Promise.all([
          fetchAnalysisRun(controller.signal),
          fetchMilestones(controller.signal).catch(() => null),
        ]);
        if (controller.signal.aborted) return;
        setRun(last);
        setMilestones(badges);
        // A run started elsewhere — the nightly pass, or a tap the person navigated away from —
        // is still theirs to see the end of.
        if (last?.status === 'running') void watchRun();
      } catch {
        /* the panel simply shows no history */
      }
    })();
    return () => controller.abort();
    // Once, on arrival: re-running this would restart the watch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();
    (async () => {
      try {
        const [queue, ev] = await Promise.all([
          fetchCheckIns(dek, controller.signal),
          fetchMoodEvidence(dek, 14, controller.signal),
        ]);
        if (!controller.signal.aborted) {
          setCheckIns(queue.checkIns);
          setTotalOpenCheckIns(queue.totalOpen);
          setEvidence(ev);
        }
      } catch {
        if (!controller.signal.aborted) {
          setCheckIns([]);
          setTotalOpenCheckIns(0);
          setEvidence(null);
        }
      }
    })();
    return () => controller.abort();
  }, [dek, portrait.isLoading]);

  function openClaim(label: string, fallbackWhy?: string) {
    const claim =
      claimByLabel.get(label.toLowerCase()) ||
      evidence?.claims.find((c) => c.label.toLowerCase().includes(label.toLowerCase()));
    if (claim) {
      setSheet({ title: claim.label, why: claim.why, sources: claim.sources });
      return;
    }
    setSheet({
      title: label,
      why: fallbackWhy || 'Not enough writing yet for Oreia to ground this in a specific entry.',
      sources: [],
    });
  }

  function openDissonance() {
    const d = evidence?.dissonance;
    setSheet({
      title: 'Tension',
      why: d?.message || mood?.dissonance.message || 'Self-report and journals diverge.',
      sources: d?.sources ?? [],
    });
  }

  /**
   * Watch a run to its end, then show what it did.
   *
   * The work happens in the background on the worker, so the result is polled rather than assumed —
   * the entire point of the button is that we report the outcome instead of guessing at it. A pass
   * that reads a backlog and calls the model can take most of a minute, so we watch for a good
   * while; if it is still going after that we say *that*, which is the one honest thing left to
   * say. Falling silently back to idle is the behaviour that made this feature look dead.
   */
  const watchRun = useCallback(async () => {
    setUpdateState('running');
    for (let attempt = 0; attempt < 26; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 2500 : 2000));
      const latest = await fetchAnalysisRun().catch(() => null);
      if (latest && latest.status !== 'running') {
        setRun(latest);
        portrait.reload();
        void reloadMood();
        setUpdateState('idle');
        return;
      }
    }
    // Still going after ~55s. It finishes on the server either way; say so and stop watching.
    portrait.reload();
    void reloadMood();
    setUpdateState('waiting');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadMood]);

  async function onUpdate() {
    if (updateState === 'waiting') {
      void watchRun();
      return;
    }
    setUpdateState('running');
    const outcome = await refreshPortrait();
    if (outcome !== 'accepted') {
      setUpdateState(outcome === 'cooldown' ? 'cooldown' : 'failed');
      setTimeout(() => setUpdateState('idle'), 5000);
      return;
    }
    await watchRun();
  }

  /**
   * Agreeing with a card files it: out of this grid, into the archive under Confirmed.
   *
   * One-way, where it used to toggle. Once a confirmed card is no longer on this screen there is
   * nothing left to toggle against, and pretending otherwise would mean a button whose second press
   * can never be reached. Taking it back is a real thing to want, but it belongs in the archive
   * next to the card — not as a hidden second state of a button that has already vanished.
   */
  async function affirm(observation: Observation) {
    if (observation.affirmed) return;
    setEdits((current) => ({ ...current, [observation.id]: { ...current[observation.id], affirmed: true } }));
    ping('Confirmed — filed in your archive');
    try {
      await respondToObservation(observation.id, 'affirm');
    } catch {
      setEdits((current) => ({
        ...current,
        [observation.id]: { ...current[observation.id], affirmed: false },
      }));
    }
  }

  async function rewrite(observation: Observation, text: string) {
    setAdjusting(null);
    setEdits((current) => ({
      ...current,
      [observation.id]: { ...current[observation.id], text, userEdited: true, citations: [] },
    }));
    ping('Memory updated in your words');
    try {
      await adjustObservation(observation.id, text);
    } catch {
      setEdits((current) => ({ ...current, [observation.id]: { ...current[observation.id], text: observation.text } }));
    }
  }

  async function forget(observation: Observation) {
    setAdjusting(null);
    setDismissed((current) => new Set(current).add(observation.id));
    ping('Forgotten — and unlearned');
    try {
      await forgetObservation(observation.id);
    } catch {
      setDismissed((current) => {
        const next = new Set(current);
        next.delete(observation.id);
        return next;
      });
    }
  }

  async function judgePortrait(key: string, verdict: 'affirm' | 'reject') {
    setCorrectingPortrait(false);
    ping(verdict === 'affirm' ? 'Affirmed — Oreia will lean on this' : "Noted — Oreia won't lead with this");
    try {
      await respondToArchetype(key, verdict);
      portrait.reload();
    } catch {
      /* the portrait simply keeps its current read */
    }
  }

  async function answerCheckIn(id: string, response: string) {
    setAnsweredCheckIns((c) => new Set(c).add(id));
    try {
      await respondToCheckIn(id, response);
    } catch {
      setAnsweredCheckIns((c) => {
        const next = new Set(c);
        next.delete(id);
        return next;
      });
    }
  }

  if (portrait.isLoading && moodLoading) {
    return (
      <div style={{ paddingTop: 60 }}>
        <Spinner label="Gathering your signals…" />
      </div>
    );
  }

  // Only what is actually sounding. Every archetype is scored on every pass, so listing the ones
  // still reading 'quiet' would fill the blend with names the writing has never supported.
  const facets = portrait.archetypes
    .filter((a) => a.status !== 'dormant' && isSounding(a))
    .sort(byProminence);
  const lead = facets[0] ?? null;
  const rest = facets.slice(1, 5);
  const threads = portrait.patterns.filter((p) => p.meaning.trim().length > 0).slice(0, 6);
  const journey = journeyFromArchetypes(portrait.archetypes);
  const emotionClaims = (evidence?.claims ?? []).slice(0, 4);
  const presence = presenceFromMood(mood, todayMood);
  const presenceClaim = claimByLabel.get(presence.label.toLowerCase());
  const hasSignals =
    (mood?.journalSampleCount ?? 0) > 0 ||
    todayMood != null ||
    observations.length > 0 ||
    threads.length > 0 ||
    symbols.length > 0 ||
    facets.length > 0 ||
    emotionClaims.length > 0;

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Self</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ahero self-hero">
        <span className="olabel akick">Archetype Profile</span>
        <h1>
          A portrait,
          <br />
          <em>in your own words.</em>
        </h1>
        {/* Directly under the title, where iOS keeps it: the answer to "is this thing still
            reading me?" is the first question this screen has to be able to answer. */}
        <UpdatePanel
          state={updateState}
          run={run}
          newEntryCount={portrait.summary?.newEntryCount ?? 0}
          onUpdate={onUpdate}
        />
      </div>

      <div className="self-split">
        <div className="self-main">
          {/* The living portrait: Oreia's read of the strongest thread, and a way to answer it. */}
          {lead && lead.read.trim() ? (
            <div className="aportrait">
              <div className="pq">{lead.read}</div>
              <div className="pmeta">
                <span className="src">
                  drawn from {lead.distinctEntryCount}{' '}
                  {lead.distinctEntryCount === 1 ? 'entry' : 'entries'}
                </span>
                <button
                  type="button"
                  className="aedit"
                  onClick={() => setCorrectingPortrait((v) => !v)}
                  aria-expanded={correctingPortrait}
                >
                  <IconSliders /> Correct
                </button>
              </div>
              {correctingPortrait ? (
                <div className="pmeta" style={{ borderTop: 0, paddingTop: 4 }}>
                  <span className="src">Does this fit?</span>
                  <span style={{ display: 'flex', gap: 7 }}>
                    <button type="button" className="actrl" onClick={() => judgePortrait(lead.key, 'affirm')}>
                      <IconCheck /> Fits
                    </button>
                    <button type="button" className="actrl" onClick={() => judgePortrait(lead.key, 'reject')}>
                      <IconClose /> Not me
                    </button>
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="aportrait">
              <div className="pq">
                Your portrait is still <em>forming</em>. Oreia would rather say nothing than say
                something it can&apos;t point back to your own entries.
              </div>
              <div className="pmeta">
                <span className="src">
                  {portrait.summary
                    ? `${portrait.summary.signals} signals so far`
                    : 'nothing drawn yet'}
                </span>
                <Link className="aedit" to="/write">
                  Write an entry
                </Link>
              </div>
            </div>
          )}

          {!hasSignals && !moodLoading ? (
            <div style={{ marginTop: 12 }}>
              <EmptyState
                title="Nothing to show you yet."
                body="Check in how you feel, or write a few entries. Oreia will not invent certainty without evidence."
                action={
                  <Link className="octa" to="/write" style={{ textDecoration: 'none' }}>
                    Write an entry
                  </Link>
                }
              />
            </div>
          ) : null}

          {/* One question, not a column of them. Answering advances in place, so someone who never
              leaves this screen still gets through the queue; the deck page is for doing several. */}
          {openCheckIns.length > 0 ? (
            <div className="ocard ocard-bright ocheckin-tile aself-block">
              <div className="ohead-row">
                <span className="olabel">Oreia is checking in</span>
                <span className="omono">
                  {openCheckIns.length} {openCheckIns.length === 1 ? 'question' : 'questions'}
                </span>
              </div>

              <CheckInCard
                key={openCheckIns[0].id}
                checkIn={openCheckIns[0]}
                onAnswer={(response) => answerCheckIn(openCheckIns[0].id, response)}
              />

              {openCheckIns.length > 1 ? (
                <Link className="ocheckin-all" to="/self/check-ins">
                  Answer all {openCheckIns.length} →
                </Link>
              ) : null}

              {totalOpenCheckIns > checkIns.length ? (
                <p className="omono ocheckin-paced">
                  {totalOpenCheckIns - checkIns.length} more noticed — held back for another day
                </p>
              ) : null}
            </div>
          ) : null}

          {observations.length > 0 ? (
            <>
              <div className="asec">
                <h2>What I hold for you</h2>
                <span className="acount2">
                  {pending.length} {pending.length === 1 ? 'memory' : 'memories'}
                </span>
              </div>

              {/* Reading the list and answering it are different jobs, and this screen has only ever
                  offered the first. The deck is where the second happens — same shape as check-ins,
                  which solved this already: browse here, decide there. */}
              {pending.length > 0 ? (
                <Link className="ocard ocard-bright oreview-cta" to="/self/review">
                  <div className="ohead-row">
                    <span className="olabel">Waiting on your word</span>
                    <span className="omono">
                      {pending.length} {pending.length === 1 ? 'card' : 'cards'}
                    </span>
                  </div>
                  <p className="obody oreview-cta-copy">
                    {pending.length === 1
                      ? 'One thing Oreia has written about you is still unanswered. Telling it what misses is how the portrait gets accurate.'
                      : `${pending.length} things Oreia has written about you are still unanswered. Telling it what misses is how the portrait gets accurate.`}
                  </p>
                  <span className="oreview-cta-go">
                    Review {pending.length === 1 ? 'it' : 'them'}
                    <IconChevronRight />
                  </span>
                </Link>
              ) : null}

              {/* Only what is still a question. Confirming a card files it, and the archive is
                  where filed things live — so this grid empties as you work through it rather than
                  growing into a list of everything Oreia has ever concluded about you. */}
              {pending.length > 0 ? (
                <div className="self-grid mem">
                  {pending.map((observation) => (
                    <MemoryCard
                      key={observation.id}
                      observation={observation}
                      entryTitle={entryTitle}
                      onAffirm={() => affirm(observation)}
                      onAdjust={() => setAdjusting(observation)}
                    />
                  ))}
                </div>
              ) : (
                <p className="aheld-clear">
                  You&apos;ve been through everything Oreia holds. What you confirmed is in the
                  archive, and new noticings will appear here.
                </p>
              )}

              <Link className="oarchive-link" to="/self/archive">
                Everything Oreia has held
                <IconChevronRight />
              </Link>
            </>
          ) : null}

          {/* Mood: the instruments the portrait is drawn from. Every tile opens its evidence. */}
          <div className="asec">
            <h2>How you&apos;ve been</h2>
            <span className="acount2">
              {mood?.journalSampleCount ? `${mood.journalSampleCount} journal ${mood.journalSampleCount === 1 ? 'signal' : 'signals'}` : 'self + journals'}
            </span>
          </div>

          <Link className="ocard ocard-deep ocard-link omood-cta" to="/self/mood">
            
            <div className="omood-cta-body">
              <div className="omood-cta-kicker">
                <span className="olabel">Mood ritual</span>
                <span className="omono">{todayMood ? `today · ${todayMood}` : 'check in'}</span>
              </div>
              <div className="omood-cta-prompt">
                <h3 className="otitle omood-cta-title">How do you feel<br />right now?</h3>
                <div className="omood-ripple" aria-hidden="true"><i /><i /><i /><i /><i /></div>
              </div>
              <p className="obody omood-cta-copy">
                A quiet moment for your mood, your energy, and what’s on your mind.
              </p>
              <span className="omood-cta-action">Begin a check-in <IconArrow /></span>
            </div>
          </Link>

          <div className="obento" style={{ marginTop: 14 }}>
            <div className="ocard ocard-bright span2">
              <div className="omood-cta-kicker" style={{ marginBottom: 8 }}>
                <span className="olabel">Emotional shift</span>
                <span className="omono">last 14 days</span>
              </div>
              <p className="obody" style={{ margin: '0 0 8px', fontSize: 13.5, color: 'var(--ink-soft)' }}>
                Notice how your feelings have shifted across recent entries.
              </p>
              {mood && mood.weather.length > 0 ? (
                <EmotionalShiftChart weather={mood.weather} />
              ) : (
                <p className="obody" style={{ margin: 0 }}>
                  Check in and write — the chart fills as both sides arrive.
                </p>
              )}
            </div>

            <button
              type="button"
              className="ocard ocard-deep omood-claim-card"
              onClick={() => openClaim(presence.label, presenceClaim?.why || presence.subtitle)}
            >
              <span className="olabel">Today I feel</span>
              <MoodPresenceMandala
                label={presence.label}
                percent={presence.percent}
                subtitle={
                  presenceClaim
                    ? `${presenceClaim.why} Tap for entries.`
                    : `${presence.subtitle} Tap for evidence.`
                }
              />
            </button>

            <div className="ocard ocard-bright span2">
              <div className="ohead-row" style={{ marginBottom: 8 }}>
                <span className="olabel">You said · journals reveal</span>
                <span className="omono">spectrum</span>
              </div>
              <p className="obody" style={{ margin: '0 0 10px', fontSize: 13.5, color: 'var(--ink-soft)' }}>
                Two paths on the same scales — divergence is the signal.
              </p>
              <MoodSpectrumPath todayMood={todayMood} journalValence={mood?.journalNetValence ?? null} />
            </div>

            {(mood?.dissonance.present || evidence?.dissonance.present) &&
            (mood?.dissonance.message || evidence?.dissonance.message) ? (
              <button
                type="button"
                className="ocard ocard-deep span2 odissonance omood-claim-card"
                onClick={openDissonance}
              >
                <CardArt variant="insight" />
                <span className="olabel" style={{ color: 'var(--accent-1)' }}>
                  Tension · why
                </span>
                <h3 className="otitle" style={{ fontSize: 22, marginTop: 10 }}>
                  {evidence?.dissonance.message || mood?.dissonance.message}
                </h3>
                <p className="obody" style={{ marginTop: 10, marginBottom: 0 }}>
                  Tap to see which entries pulled the journal path the other way.
                </p>
              </button>
            ) : null}

            {emotionClaims.map((claim) => (
              <button
                type="button"
                className="ocard ocard-light omood-claim-card"
                key={claim.canonicalId}
                onClick={() => setSheet({ title: claim.label, why: claim.why, sources: claim.sources })}
              >
                <CardArt variant="insight" />
                <span className="olabel">emotion</span>
                <div className="oserif" style={{ fontSize: 22, marginTop: 8, lineHeight: 1.15 }}>
                  {claim.label}
                </div>
                <p className="obody" style={{ margin: '10px 0 0', fontSize: 13 }}>
                  {claim.why}
                </p>
                <div className="ometa" style={{ marginTop: 8 }}>
                  <span className="omono">
                    From {claim.sources.length} {claim.sources.length === 1 ? 'entry' : 'entries'} →
                  </span>
                </div>
              </button>
            ))}

            <Link className="ocard ocard-bright span2 ocard-link" to="/mirror">
              <CardArt variant="insight" />
              <div className="ohead-row">
                <span className="olabel">Reports</span>
                <span className="omono">Weekly Mirror →</span>
              </div>
              <p className="obody" style={{ margin: '10px 0 0' }}>
                Time-boxed readings — they expire; they are not who you are.
              </p>
            </Link>
          </div>

          {symbols.length > 0 ? (
            <>
              <div className="asec">
                <h2>Symbol Vault</h2>
                <Link className="acount2 osym-all" to="/symbols">
                  see all {symbols.length} →
                </Link>
              </div>
              <div className="ocard ocard-light">
                <div className="osyms">
                  {symbols.slice(0, 12).map((symbol) => (
                    <Link
                      className="osym osym-link"
                      key={symbol.name}
                      to={`/symbols/${encodeURIComponent(symbol.name)}`}
                    >
                      <SymbolOrb name={symbol.name} />
                      <div className="snm">{symbol.name}</div>
                      <div className="smono">×{symbol.count}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {journey.length > 0 ? (
            <>
              <div className="asec">
                <h2>Your journey</h2>
                <span className="acount2">what appeared, when</span>
              </div>
              <div className="ablend" style={{ padding: 22 }}>
                <div className="ajourney">
                  {journey.map((step) => (
                    <div className={`ajstep${step.isNow ? ' now' : ''}`} key={step.id}>
                      <span className="dot" aria-hidden />
                      <div className="jwhen">{step.when}</div>
                      <div className="jtext">
                        {step.before}
                        <em>{step.pivot}</em>
                        {step.after}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Side rail — sticky from 1080px, stacked below on smaller screens. */}
        <aside className="self-side">
          {lead ? (
            <>
              <div className="asec" style={{ marginTop: 26 }}>
                <h2>Your blend</h2>
                <span className="acount2">
                  {facets.length} {facets.length === 1 ? 'facet' : 'facets'}
                </span>
              </div>
              {/* Levels, not percentages: the score behind these bars is normalised so the top
                  archetype is always the maximum, and printing that as "100%" would be a confident
                  number about nothing. The word is the claim; the bar is only its shape. */}
              <div className="ablend">
                <div className="lead">
                  <span className="nm oserif">{lead.name}</span>
                  <span className="pct">{ACTIVATION_WORD[lead.level]}</span>
                </div>
                <p className="leadsub">
                  Soft lenses from your writing, not a permanent identity — and not a quiz result.
                </p>
                {rest.map((facet) => (
                  <div className="arow" key={facet.key}>
                    <span className="anm">{facet.name}</span>
                    <div
                      className="ometer"
                      role="img"
                      aria-label={`${facet.name}: ${ACTIVATION_WORD[facet.level]}`}
                    >
                      <i style={{ width: `${facet.activation}%` }} />
                    </div>
                    <span className="apct">{ACTIVATION_WORD[facet.level]}</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {milestones && milestones.milestones.length > 0 ? (
            <Link className="abadges" to="/self/milestones" style={{ marginTop: 14 }}>
              <span className="stack" aria-hidden>
                {milestones.milestones.slice(0, 3).map((m) => (
                  <span className="bdg-coin" key={m.key}>
                    <IconOreia />
                  </span>
                ))}
              </span>
              <span className="bi">
                <span className="bn">Milestones</span>
                <span className="bd">
                  {milestones.earnedCount} of {milestones.milestones.length} gathered
                </span>
              </span>
              <IconChevronRight />
            </Link>
          ) : null}

          {threads.length > 0 ? (
            <>
              <div className="asec">
                <h2>Threads you&apos;re working</h2>
                <span className="acount2">gentle</span>
              </div>
              <div className="ocard ocard-light" style={{ padding: '6px 18px' }}>
                {threads.map((thread) => {
                  const name = patternLabel(thread.canonicalId);
                  return (
                    <Link
                      className="athread"
                      key={thread.canonicalId}
                      to={`/symbols/${encodeURIComponent(name)}`}
                    >
                      <div className="tbody">
                        <div className="tnm oserif">{name}</div>
                        <div className="tsub">
                          {thread.occurrences} {thread.occurrences === 1 ? 'entry' : 'entries'} ·{' '}
                          {thread.meaning}
                        </div>
                      </div>
                      <div className="tarc">
                        <div
                          className="ometer"
                          role="progressbar"
                          aria-valuenow={thread.occurrences}
                          aria-valuemin={0}
                          aria-valuemax={10}
                          aria-label={`${name}: ${threadState(thread.trajectory, thread.occurrences)}`}
                        >
                          <i style={{ width: `${Math.min(100, thread.occurrences * 12)}%` }} />
                        </div>
                        <span className="tlabel2">
                          {threadState(thread.trajectory, thread.occurrences)}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          ) : null}

          <div className="aprivacy">
            <IconLock />
            <div className="pt">
              <b>This portrait lives only with you.</b> Every memory is encrypted, traceable to your
              entries, and yours to edit or erase — nothing here is shared, ever.
            </div>
          </div>
        </aside>
      </div>

      {adjusting ? (
        <AdjustMemorySheet
          observation={adjusting}
          entryTitle={entryTitle}
          onSave={(text) => rewrite(adjusting, text)}
          onForget={() => forget(adjusting)}
          onClose={() => setAdjusting(null)}
        />
      ) : null}

      {sheet ? (
        <EvidenceSheet
          title={sheet.title}
          why={sheet.why}
          sources={sheet.sources}
          entryTitle={entryTitle}
          onClose={() => setSheet(null)}
        />
      ) : null}

      <JournalToast text={toast} />
      <div style={{ height: 60 }} />
    </>
  );
}

function EvidenceSheet({
  title,
  why,
  sources,
  entryTitle,
  onClose,
}: {
  title: string;
  why: string;
  sources: MoodEvidenceSource[];
  entryTitle: (id: string) => string | null;
  onClose: () => void;
}) {
  return (
    <div className="oevidence-scrim" role="dialog" aria-modal="true" aria-label={`Evidence for ${title}`}>
      <div className="oevidence-sheet">
        <div className="ohead-row">
          <span className="olabel">Why Oreia says this</span>
          <button type="button" className="ochip" onClick={onClose}>
            Close
          </button>
        </div>
        <h3 className="otitle" style={{ fontSize: 24, marginTop: 10 }}>
          {title}
        </h3>
        <p className="obody" style={{ marginTop: 10 }}>
          {why}
        </p>
        {sources.length === 0 ? (
          <p className="ometa" style={{ marginTop: 16 }}>
            Not enough writing yet to cite a specific entry.
          </p>
        ) : (
          <div className="ostack" style={{ gap: 10, marginTop: 18 }}>
            {sources.map((s) => (
              <Link
                key={`${s.entryId}-${s.detectedAt}`}
                className="ocard ocard-light ocard-link"
                to={`/write/${encodeURIComponent(s.entryId)}`}
                onClick={onClose}
              >
                <div className="ohead-row">
                  <span className="olabel">{entryTitle(s.entryId) || s.surfaceForm || 'Entry'}</span>
                  <span className="omono">{(s.detectedAt || '').slice(0, 10)}</span>
                </div>
                {s.snippet ? (
                  <p className="obody" style={{ margin: '8px 0 0', fontSize: 14 }}>
                    “{s.snippet}”
                  </p>
                ) : (
                  <p className="ometa" style={{ marginTop: 8 }}>
                    Open entry to read the passage Oreia used.
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
